/**
 * SN-8475: the duplicate review's choice for each flagged row, and the two
 * boxes that set every row in one go: "Create a new player for all rows" and
 * "Select the first listed player for all rows".
 */

/** A new profile, no import at all, or an existing player by uid. */
export type DuplicateDecision = 'create' | 'ignore' | `existing:${string}`

export interface DuplicateCandidate {
  uid: string
}

/** The fields of a flagged row (`conflicts.conflicts[]` in the 409) that the decisions read. */
export interface DuplicateConflict {
  line: number
  first_name: string
  last_name: string
  player_conflicts: DuplicateCandidate[]
  /**
   * The candidates already on the row's target roster, which the review lists
   * first. Unordered; an older API does not send it.
   */
  on_target_roster_uids?: string[]
}

export type DecisionsByLine = Readonly<Record<number, DuplicateDecision>>

/** The two bulk boxes: every row to a new player, or to its first listed player. */
export type BulkKind = 'create' | 'firstListed'

/** What a bulk box sets, by line; a line it cannot set is absent. */
export type BulkTargets = ReadonlyMap<number, DuplicateDecision>

/**
 * `bulk` is the box the admin ticked and `snapshot` the decisions from before
 * the first tick. Both are cleared together, by a pick made by hand or by
 * unticking, so a box is never ticked by matching the rows by hand.
 */
export type DuplicateDecisionsState =
  | { decisions: DecisionsByLine; bulk: null; snapshot: null }
  | { decisions: DecisionsByLine; bulk: BulkKind; snapshot: DecisionsByLine }

export type DuplicateDecisionsAction =
  | { type: 'decide'; line: number; decision: DuplicateDecision }
  | { type: 'applyToAll'; kind: BulkKind; targets: BulkTargets }
  | { type: 'undoApplyToAll' }

const EXISTING_PREFIX = 'existing:'

export const existingDecision = (uid: string): DuplicateDecision =>
  `${EXISTING_PREFIX}${uid}`

/** The uid an `existing:<uid>` decision points at; null for create and ignore. */
export const existingUid = (
  decision: DuplicateDecision | undefined
): string | null =>
  decision?.startsWith(EXISTING_PREFIX)
    ? decision.slice(EXISTING_PREFIX.length)
    : null

/**
 * The row's candidates in the order the review lists them: the players
 * already on its target roster first, then the rest, each group in the API's
 * order. The on-roster list only sorts; a uid the row does not offer is never
 * listed.
 */
export const orderedCandidates = (
  conflict: DuplicateConflict
): DuplicateCandidate[] => {
  const onRoster = new Set(conflict.on_target_roster_uids ?? [])
  const here: DuplicateCandidate[] = []
  const elsewhere: DuplicateCandidate[] = []

  conflict.player_conflicts.forEach((candidate) => {
    if (onRoster.has(candidate.uid)) {
      here.push(candidate)
    } else {
      elsewhere.push(candidate)
    }
  })

  return [...here, ...elsewhere]
}

/**
 * What a bulk box sets on each row: a new player, or the row's first listed
 * player. A row with no candidate has no first listed player and is left out.
 */
export const bulkTargets = (
  conflicts: readonly DuplicateConflict[],
  kind: BulkKind
): BulkTargets => {
  const targets = new Map<number, DuplicateDecision>()

  conflicts.forEach((conflict) => {
    if (kind === 'create') {
      targets.set(conflict.line, 'create')
      return
    }

    const [first] = orderedCandidates(conflict)
    if (first !== undefined) {
      targets.set(conflict.line, existingDecision(first.uid))
    }
  })

  return targets
}

export const isComplete = (
  decisions: DecisionsByLine,
  conflicts: readonly DuplicateConflict[]
): boolean => conflicts.every(({ line }) => decisions[line] !== undefined)

const countOf = (decisions: DecisionsByLine, kind: DuplicateDecision): number =>
  Object.values(decisions).filter((decision) => decision === kind).length

/** Rows left out of the import. */
export const ignoredCount = (decisions: DecisionsByLine): number =>
  countOf(decisions, 'ignore')

export const INITIAL_DUPLICATE_DECISIONS: DuplicateDecisionsState = {
  decisions: {},
  bulk: null,
  snapshot: null,
}

export const duplicateDecisionsReducer = (
  state: DuplicateDecisionsState,
  action: DuplicateDecisionsAction
): DuplicateDecisionsState => {
  switch (action.type) {
    case 'decide':
      return {
        decisions: { ...state.decisions, [action.line]: action.decision },
        bulk: null,
        snapshot: null,
      }
    case 'applyToAll': {
      const decisions: Record<number, DuplicateDecision> = {
        ...state.decisions,
      }
      action.targets.forEach((target, line) => {
        decisions[line] = target
      })

      return {
        decisions,
        bulk: action.kind,
        // Switching boxes keeps the decisions from before the first tick.
        snapshot: state.bulk === null ? state.decisions : state.snapshot,
      }
    }
    case 'undoApplyToAll':
      return state.bulk === null
        ? state
        : { decisions: state.snapshot, bulk: null, snapshot: null }
    default:
      return state
  }
}
